<template>
  <div class="annotation-toolbar" :class="{ 'annotation-toolbar--pill': variant === 'pill' }">
    <div class="annotation-toolbar__tools">
      <!-- A tool that can't be used yet stays hoverable (aria-disabled, not disabled), so its tooltip
           can say why -->
      <fs-tooltip
        v-for="tool in visibleTools"
        :key="tool.id"
        is-delayed
        :text="tool.disabledReason ?? tool.label"
        show-even-if-not-ellipsed
        :class="`annotation-toolbar__tool-wrap annotation-toolbar__tool-wrap--${tool.id}`"
      >
        <button
          type="button"
          class="annotation-toolbar__tool"
          :class="{
            'annotation-toolbar__tool--active': activeTool === tool.id,
            'annotation-toolbar__tool--disabled': !!tool.disabledReason,
          }"
          :aria-label="tool.label"
          :aria-disabled="!!tool.disabledReason"
          @click="!tool.disabledReason && emit('update:activeTool', tool.id)"
        >
          <span class="annotation-toolbar__tool-icon" v-html="tool.icon" />
        </button>
      </fs-tooltip>

      <!-- In the pill the colour and width sit behind a swatch, to keep the toolbar on one line. -->
      <template v-if="variant === 'pill' && showStyleControls">
        <fs-tooltip is-delayed text="Colour and width" show-even-if-not-ellipsed>
          <button
            type="button"
            class="annotation-toolbar__tool"
            aria-label="Colour and width"
            @click="stylePopper?.toggle($event)"
          >
            <span class="annotation-toolbar__swatch" :style="{ backgroundColor: activeStyle.color }" />
          </button>
        </fs-tooltip>
        <fs-popper ref="stylePopper" placement="bottom-end" :offset="[0, 8]">
          <annotation-style-controls
            class="annotation-toolbar__style-popper"
            :model-value="activeStyle"
            @update:model-value="emit('update:activeStyle', $event)"
          />
        </fs-popper>
      </template>
    </div>
    <annotation-style-controls
      v-if="variant !== 'pill' && showStyleControls"
      class="annotation-toolbar__style"
      :model-value="activeStyle"
      @update:model-value="emit('update:activeStyle', $event)"
    />
  </div>
</template>

<script lang="ts">
import type { AnnotationGeometry } from '@/ApiClient';
import markerIcon from '@icons/annotations/marker.svg';
import arrowIcon from '@icons/annotations/arrow.svg';
import rectangleIcon from '@icons/annotations/rectangle.svg';
import ellipseIcon from '@icons/annotations/ellipse.svg';
import freehandIcon from '@icons/annotations/freehand.svg';
import highlighterIcon from '@icons/annotations/highlighter.svg';
import freeCommentIcon from '@icons/other2/text-editor.svg';

export type AnnotationTool = 'marker' | 'comment' | 'arrow' | 'rectangle' | 'ellipse' | 'freehand' | 'highlighter';

// Tools that place points rather than draw shapes: no colour or width, and points stay selectable.
export const POINT_TOOLS: AnnotationTool[] = ['marker', 'comment'];

// Also how a drawn shape is named where marks are listed
export const ANNOTATION_TOOL_LABELS: Record<AnnotationTool, string> = {
  marker: 'Marker',
  comment: 'Comment',
  arrow: 'Arrow',
  rectangle: 'Rectangle',
  ellipse: 'Ellipse',
  freehand: 'Pen',
  highlighter: 'Highlighter',
};

// In toolbar order
export const ANNOTATION_TOOL_ICONS: Record<AnnotationTool, string> = {
  marker: markerIcon,
  comment: freeCommentIcon,
  arrow: arrowIcon,
  rectangle: rectangleIcon,
  ellipse: ellipseIcon,
  freehand: freehandIcon,
  highlighter: highlighterIcon,
};

// The tool each drawn shape comes from; geometries not listed are placed points, lines and curves
export const SHAPE_TOOL_BY_GEOMETRY: Partial<Record<AnnotationGeometry['type'], AnnotationTool>> = {
  ArrowGeometry: 'arrow',
  RectangleGeometry: 'rectangle',
  EllipseGeometry: 'ellipse',
  FreehandGeometry: 'freehand',
  HighlighterGeometry: 'highlighter',
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { AnnotationStyle } from '@/ApiClient';
import FsPopper from '@components/poppers/fs-popper.vue';
import FsTooltip from '@components/poppers/fs-tooltip.vue';
import AnnotationStyleControls from './annotation-style-controls.vue';

const {
  activeTool,
  activeStyle,
  availableTools,
  variant,
  withFreeComment = false,
  disabledTools,
} = defineProps<{
  activeTool: AnnotationTool;
  activeStyle: AnnotationStyle;
  availableTools?: AnnotationTool[];
  // Adds the tool for free comments: markers tied to no point
  withFreeComment?: boolean;
  // Tools that can't be picked right now, each with the reason shown in its tooltip
  disabledTools?: Partial<Record<AnnotationTool, string>>;
  // Round buttons in a grey pill, for a toolbar that shares its line with other controls
  variant?: 'pill';
}>();

const stylePopper = ref<any>(null);

const emit = defineEmits<{
  'update:activeTool': [tool: AnnotationTool];
  'update:activeStyle': [style: AnnotationStyle];
}>();

const allTools = (Object.keys(ANNOTATION_TOOL_ICONS) as AnnotationTool[]).map((id) => ({
  id,
  icon: ANNOTATION_TOOL_ICONS[id],
}));

const visibleTools = computed(() =>
  allTools
    .filter((t) => (availableTools ? availableTools.includes(t.id) : true) && (t.id !== 'comment' || withFreeComment))
    .map((t) => ({ ...t, label: ANNOTATION_TOOL_LABELS[t.id], disabledReason: disabledTools?.[t.id] }))
);

const showStyleControls = computed(() => !POINT_TOOLS.includes(activeTool));
</script>

<style scoped lang="scss">
@import '@styles/variables';

.annotation-toolbar {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 0.6rem 1.2rem;
  background: $c-bg-light;
  border-bottom: 1px solid rgba($c-dark, 0.1);

  &__tools {
    display: flex;
    gap: 0.2rem;
  }

  &__tool {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3.2rem;
    height: 3.2rem;
    border: 1px solid transparent;
    border-radius: 0.4rem;
    background: none;
    cursor: pointer;
    font-size: 1.6rem;
    color: $c-dark;

    &:not(&--disabled):hover {
      background: rgba($c-dark, 0.06);
    }

    &--active {
      background: rgba($c-blue, 0.1);
      border-color: $c-blue;
      color: $c-blue;
    }

    &--disabled {
      opacity: 0.35;
      cursor: default;
    }
  }

  &__tool-icon {
    display: flex;

    :deep(svg) {
      width: 1.8rem;
      height: 1.8rem;
      fill: $c-dark;
    }
  }

  &__tool--active &__tool-icon :deep(svg) {
    fill: $c-blue;
  }

  &__style {
    margin-left: 0.8rem;
    padding-left: 1.2rem;
    border-left: 1px solid rgba($c-dark, 0.1);
  }

  &__swatch {
    width: 1.4rem;
    height: 1.4rem;
    border-radius: 50%;
    box-shadow: 0 0 0 2px $c-surface;
  }

  &__style-popper {
    padding: 1.2rem 1.4rem;
  }

  &--pill {
    padding: 0;
    border-bottom: none;
    background: none;

    .annotation-toolbar__tools {
      padding: 0.2rem;
      border-radius: 9999px;
      background: $c-surface-muted;
    }

    .annotation-toolbar__tool {
      width: 2.8rem;
      height: 2.8rem;
      border: none;
      border-radius: 50%;

      &:not(.annotation-toolbar__tool--disabled):hover {
        background: rgba($c-dark, 0.08);
      }
    }

    .annotation-toolbar__tool-icon :deep(svg) {
      width: 1.4rem;
      height: 1.4rem;
      opacity: 0.85;
    }

    .annotation-toolbar__tool--active,
    .annotation-toolbar__tool--active:hover {
      background: $c-ink;

      .annotation-toolbar__tool-icon :deep(svg) {
        fill: $c-surface;
        opacity: 1;
      }
    }
  }
}
</style>
